import { useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { CREATOR_ROWS, creatorLookField, creatorOffer, creatorStart, type Appearance } from "odyssey-shared";
import { ModalWindow } from "../../ui/kit/index.ts";
import { say } from "../../words.ts";
import { lookWindowBox } from "../look-preview/layout.ts";
import { LookPanel, pageSeed, type LookRefusal } from "../look-preview/look-panel.tsx";
import { windowPlace, windowSize } from "../windows/places.ts";
import { VoiceChoice } from "../voice-choice.tsx";
import { lookRefusal, sameAppearance, type LookScreenPage } from "./look-screen.ts";

/**
 * The temporary Change look window (ADR-094's amendment of 2026-10-06, B12 and B13; R-0221): the Look panel in the
 * kit's modal window, so a system window behind its scrim, moved and resized through the kit's frame (ADR-061's amendment "every game window moves and resizes": its place and size are kept on this device under `look`), opened over the game menu and backing out to it by
 * Cancel, its close button or Escape. It is titled with the character's name, shows the
 * five rows and the Voice choice after them (ADR-094 note 126), and opens on the character's own look and voice, with
 * Confirm unavailable until the look or the voice differs from them. On a page that holds no look for its character
 * the window opens on a look the creator picks, and Confirm waits for a change from that, so nothing the player did
 * not choose is ever sent. A voice changed here is the character's from its next join (note 127).
 *
 * Confirm sends the three keys and the voice through the page, as one change. While the request is out Cancel, Randomise and Confirm refuse and
 * the window does not close. An accepted change closes the window to the world, where the map server shows it. A
 * refusal keeps the window and the choices and shows its sentence. A session that is gone shows nothing here: the
 * page has left the world for it. The component imports no stylesheet; `mount.tsx` imports the sheets.
 */

/**
 * The Change look window.
 *
 * @param props `page`, what the screen needs from the page; `restUntil`, the time on the page's clock before which
 *   Confirm rests after an accepted change; `onBack`, called when the window leaves for the menu; and `onDone`,
 *   called once a change was accepted.
 * @returns The window.
 * @throws Error when mounted outside a `KitInputProvider`.
 * @example
 * <LookWindow page={look} restUntil={0} onBack={() => onEvent({ type: "backed" })} onDone={() => onEvent({ type: "lookChanged" })} />
 */
export function LookWindow({ page, restUntil, onBack, onDone }: { readonly page: LookScreenPage; readonly restUntil: number; readonly onBack: () => void; readonly onDone: () => void }): ReactElement {
  const now = page.now ?? Date.now;
  const offer = useMemo(() => creatorOffer(page.looks), [page.looks]);
  const [opened] = useState(() => {
    const current = page.current();
    return { current, look: creatorStart(offer, current, pageSeed()), voice: page.voice(), source: page.source(), name: page.name() };
  });
  const [look, setLook] = useState<Appearance>(opened.look);
  const [voice, setVoice] = useState<string | null>(opened.voice);
  const [told, setTold] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [refusal, setRefusal] = useState<LookRefusal | null>(null);
  const [resting, setResting] = useState(() => now() < restUntil);
  const mounted = useRef(true);
  const out = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!resting) {
      return undefined;
    }
    const timer = setTimeout(() => setResting(false), Math.max(0, restUntil - now()));
    return () => clearTimeout(timer);
  }, [resting, restUntil, now]);

  const same = sameAppearance(look, opened.current ?? opened.look) && voice === opened.voice;

  /**
   * Sends the look and the voice shown and acts on the answer; does nothing while a request is out, while the look
   * is the character's own or the one the window opened on with none held and the voice the one it opened on, or
   * while Confirm rests. The request is marked
   * out as it is sent, not when the window is next drawn, so two presses in one moment send one. An answer that
   * arrives after the window has gone is dropped.
   */
  const confirm = (): void => {
    if (out.current || same || resting) {
      return;
    }
    out.current = true;
    setSaving(true);
    setRefusal(null);
    void page.change(creatorLookField(look), voice).then((outcome) => {
      if (!mounted.current) {
        return;
      }
      if (outcome.ok) {
        onDone();
        return;
      }
      out.current = false;
      setSaving(false);
      if (outcome.why !== "lost") {
        setRefusal(lookRefusal(outcome));
      }
    });
  };

  /**
   * Leaves for the menu, unless a request is out.
   */
  const back = (): void => {
    if (!saving) {
      onBack();
    }
  };

  return (
    <LookPanel
      frame={({ title, footer, children }) => (
        <ModalWindow id="look" place={windowPlace("look")} size={windowSize("look")} title={title} footer={footer} onClose={back} fit={lookWindowBox}>
          {children}
        </ModalWindow>
      )}
      words={{ title: say("look.change.title", { name: opened.name }), back: say("look.change.cancel"), randomise: say("look.change.randomise"), primary: say("look.change.confirm"), saving: say("look.change.saving") }}
      rows={CREATOR_ROWS}
      offer={offer}
      look={look}
      onLook={(next) => {
        setLook(next);
        setRefusal(null);
        setTold(null);
      }}
      note={told}
      after={
        <VoiceChoice
          value={voice}
          disabled={saving}
          onChange={(next) => {
            setVoice(next);
            setRefusal(null);
            setTold(null);
          }}
          onPreview={() => setTold(page.hear(voice) ? null : say("account.voice.noSample"))}
        />
      }
      source={opened.source}
      saving={saving}
      primaryDisabled={same || resting}
      refusal={refusal}
      touch={page.touch === true}
      onBack={back}
      onPrimary={confirm}
    />
  );
}
